/**
 * InstaGain's mascot — the nerd in a speech bubble from the logo — drawn as a vector so it
 * can change its face.
 *
 * It is the panel's illustration system: instead of stock pictures, every empty screen,
 * the sign-in and the greetings show the same character in the mood of the moment. It is
 * decorative everywhere it appears: the text next to it always says the same thing.
 */

export type MascotMood = "happy" | "idle" | "sleepy" | "wow" | "searching" | "party" | "wink" | "worried";

interface MascotProps {
  mood?: MascotMood;
  size?: number;
  className?: string;
  /** A gentle float, for the hero spots; off in lists where it would distract. */
  float?: boolean;
}

const INK = "#3a2618";
const SKIN = "#e2c9a6";
const BLUSH = "#e8a586";

export function Mascot({ mood = "happy", size = 120, className, float = false }: MascotProps) {
  return (
    <svg
      className={`cs-mascot${float ? " cs-mascot-float" : ""}${className ? ` ${className}` : ""}`}
      width={size}
      height={size * (112 / 124)}
      viewBox="0 0 124 112"
      aria-hidden="true"
      focusable="false"
    >
      {/* The bubble, tail down and to the left as in the logo. */}
      <path
        d="M34 6h56a28 28 0 0 1 28 28v24a28 28 0 0 1-28 28H58L41 104c-2.4 2.6-5 1.6-5-1.4V86h-2A28 28 0 0 1 6 58V34A28 28 0 0 1 34 6Z"
        fill={SKIN}
        stroke={INK}
        strokeOpacity="0.08"
        strokeWidth="2"
      />
      {mood === "happy" || mood === "party" || mood === "wink" ? (
        <>
          <ellipse cx="26" cy="64" rx="6" ry="3.5" fill={BLUSH} opacity="0.55" />
          <ellipse cx="98" cy="64" rx="6" ry="3.5" fill={BLUSH} opacity="0.55" />
        </>
      ) : null}

      {/* The glasses. */}
      <g fill="none" stroke={INK} strokeWidth="5.5" strokeLinejoin="round">
        <rect x="21" y="30" width="35" height="27" rx="10" />
        <rect x="68" y="30" width="35" height="27" rx="10" />
        <path d="M56 38c4-3 8-3 12 0" />
        <path d="M21 33h-5M103 33h5" strokeLinecap="round" />
      </g>

      <Eyes mood={mood} />
      <Mouth mood={mood} />
      <Extras mood={mood} />
    </svg>
  );
}

function Eyes({ mood }: { mood: MascotMood }) {
  const stroke = { fill: "none", stroke: INK, strokeWidth: 4.2, strokeLinecap: "round" as const };
  switch (mood) {
    case "happy":
    case "party":
      return (
        <>
          <path d="M31 47c2.5-6 10.5-6 13 0" {...stroke} />
          <path d="M78 47c2.5-6 10.5-6 13 0" {...stroke} />
        </>
      );
    case "wink":
      return (
        <>
          <path d="M31 47c2.5-6 10.5-6 13 0" {...stroke} />
          <g className="cs-mascot-eye">
            <circle cx="85" cy="44" r="4" fill={INK} />
          </g>
        </>
      );
    case "sleepy":
      return (
        <>
          <path d="M31 43c2.5 5 10.5 5 13 0" {...stroke} />
          <path d="M78 43c2.5 5 10.5 5 13 0" {...stroke} />
        </>
      );
    case "wow":
      return (
        <>
          <circle cx="38" cy="44" r="5.5" fill={INK} />
          <circle cx="85" cy="44" r="5.5" fill={INK} />
          <circle cx="40" cy="42" r="1.6" fill="#fff" />
          <circle cx="87" cy="42" r="1.6" fill="#fff" />
        </>
      );
    case "searching":
      return (
        <g className="cs-mascot-eye">
          <circle cx="42" cy="45" r="4" fill={INK} />
          <circle cx="89" cy="45" r="4" fill={INK} />
        </g>
      );
    case "worried":
      return (
        <>
          <circle cx="38" cy="45" r="4" fill={INK} />
          <circle cx="85" cy="45" r="4" fill={INK} />
          <path d="M30 26l12 3M94 26l-12 3" {...stroke} strokeWidth={3.4} />
        </>
      );
    default:
      return (
        <g className="cs-mascot-eye">
          <circle cx="38" cy="44" r="4" fill={INK} />
          <circle cx="85" cy="44" r="4" fill={INK} />
        </g>
      );
  }
}

function Mouth({ mood }: { mood: MascotMood }) {
  if (mood === "wow") return <ellipse cx="62" cy="71" rx="5" ry="6" fill={INK} />;
  if (mood === "sleepy") return <path d="M55 70h14" stroke={INK} strokeWidth="4" strokeLinecap="round" />;
  if (mood === "worried")
    return <path d="M52 73c5-5 15-5 20 0" fill="none" stroke={INK} strokeWidth="4" strokeLinecap="round" />;
  return (
    <>
      <path d="M49 66c8 7 18 7 26 0" fill="none" stroke={INK} strokeWidth="4.2" strokeLinecap="round" />
      {/* The two front teeth, the logo's trademark. */}
      <path
        d="M57 70.5v4.5c0 1.5 1 2.5 2.5 2.5h5c1.5 0 2.5-1 2.5-2.5v-4.5"
        fill="#fffaf2"
        stroke={INK}
        strokeWidth="2.6"
      />
      <path d="M62 71v6" stroke={INK} strokeWidth="2" />
    </>
  );
}

function Extras({ mood }: { mood: MascotMood }) {
  if (mood === "sleepy") {
    return (
      <g fill={INK} fontFamily="var(--font-display)" fontWeight="700" className="cs-mascot-zzz">
        <text x="104" y="16" fontSize="14">
          z
        </text>
        <text x="114" y="6" fontSize="10">
          z
        </text>
      </g>
    );
  }
  if (mood === "searching") {
    return (
      <g className="cs-mascot-lens">
        <circle cx="100" cy="86" r="11" fill="#fff8ee" fillOpacity="0.7" stroke={INK} strokeWidth="4.5" />
        <path d="M108 94l9 9" stroke={INK} strokeWidth="6" strokeLinecap="round" />
      </g>
    );
  }
  if (mood === "party") {
    return (
      <g className="cs-mascot-confetti">
        <rect x="8" y="2" width="6" height="6" rx="1.5" fill="#c2683f" transform="rotate(20 11 5)" />
        <rect x="108" y="0" width="6" height="6" rx="1.5" fill="#d4932c" transform="rotate(-15 111 3)" />
        <circle cx="118" cy="22" r="3" fill="#7d7b45" />
        <circle cx="3" cy="24" r="3" fill="#4f7472" />
        <path d="M100 4l3 6M20 0l-3 6" stroke="#c2683f" strokeWidth="2.5" strokeLinecap="round" />
      </g>
    );
  }
  if (mood === "worried") {
    return <path d="M110 30c3 4 3 8 0 9-3-1-3-5 0-9Z" fill="#7fb2c9" />;
  }
  return null;
}
